import React, { useEffect, useState, useContext } from 'react';
import cn from 'classnames';
import { http, authHeaders, ProfileContext } from '@loyalup/core';
import { WithTitle, DatePeriodPicker } from '@loyalup/ui';
import { useTranslation } from 'react-i18next';
import {
  parseISO,
  startOfMonth,
  endOfMonth,
  differenceInDays,
  isSameHour,
  isSameDay,
  isSameWeek,
  addHours,
  addDays,
  addWeeks,
  addMonths,
  addYears,
  isAfter,
  format,
} from 'date-fns';

import PassesChart from './PassesChart';
import OrdersChart from './OrdersChart';

import s from './Stats.module.css';

const CHART_NAMES = {
  deleted: 'Деактивировано',
  added: 'Активировано',
  issued: 'Выпущено',
  once: '1 заказ',
  multi: '2 и более',
};
const CHART_COLORS = {
  deleted: '#f5b945',
  added: '#5a22d2',
  issued: '#4a88da',
};

const periodIncrementer = ({ start, end }) => {
  const days = differenceInDays(end, start);
  switch (true) {
    case days < 2:
      return [addHours, isSameHour, 3];
    case days < 7 * 2:
      return [addDays, isSameDay, 1];
    case days < 30 * 2:
      return [addWeeks, isSameDay, 1];
    case days < 365 * 2:
      return [addMonths, isSameWeek, 1];
    default:
      return [addYears, isSameWeek, 1];
  }
};

const findPeriodIntervals = period => {
  const [incFn, equalFn, inc] = periodIncrementer(period);
  const intervals = [period.start];
  while (true) {
    const last = intervals[intervals.length - 1];
    const next = incFn(last, inc);
    if (isAfter(next, period.end)) break;
    intervals.push(next);
  }

  if (equalFn(period.end, intervals[intervals.length - 1])) intervals.pop();

  intervals.push(period.end);
  return intervals;
};

const loadCharts = async (t, period, setPassesChart, setOrdersChart) => {
  const intervals = findPeriodIntervals(period);
  const headers = await authHeaders();

  loadPassesChart(t, intervals, headers, setPassesChart);
  loadOrdersChart(t, intervals, headers, setOrdersChart);
};

const loadPassesChart = async (t, intervals, headers, setChart) => {
  const { success, charts, diff } = await http('POST', '/charts/passes', { i: intervals }, headers);

  const chartData = (charts || []).map(({ id, data }) => ({
    id: t(CHART_NAMES[id] || 'Unknown'),
    color: CHART_COLORS[id],
    data: data.map(([date, value]) => ({ x: parseISO(date + 'Z'), y: value })),
  }));

  setChart(chart => ({
    ...chart,
    loading: false,
    success,
    diff,
    data: chartData,
  }));
};

const loadOrdersChart = async (t, intervals, headers, setChart) => {
  const { success, rows, total, once, multi } = await http('POST', '/charts/orders', { i: intervals }, headers);

  const onceKey = t(CHART_NAMES.once || 'Once');
  const multiKey = t(CHART_NAMES.multi || 'Multi');

  const keys = [onceKey, multiKey];

  const chartData = (rows || []).map(([date, once, multi]) => ({
    period: format(parseISO(date + 'Z'), 'dd.MM'),
    onceKey: onceKey,
    [`${onceKey}Color`]: '#5a22d2',
    [`${multiKey}Color`]: '#f5b945',
    [onceKey]: once,
    [multiKey]: multi,
  }));

  setChart(chart => ({
    ...chart,
    loading: false,
    success,
    total,
    keys,
    once,
    multi,
    data: chartData,
  }));
};

const Stats = () => {
  const [t] = useTranslation();
  const { clientsCount } = useContext(ProfileContext);
  const [period, setPeriod] = useState({ start: startOfMonth(new Date()), end: endOfMonth(new Date()) });

  const [passesChart, setPassesChart] = useState({
    loading: true,
    success: true,
    diff: 0,
    max: clientsCount || 20,
    data: [],
  });

  const [ordersChart, setOrdersChart] = useState({
    loading: true,
    success: true,
    total: 0,
    once: 0,
    multi: 0,
    max: 20,
    keys: [],
    data: [],
  });

  useEffect(() => {
    loadCharts(t, period, setPassesChart, setOrdersChart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  return (
    <WithTitle title={t('Статистика')}>
      <div className={s.header}>
        <h1>{t('Статистика')}</h1>
        <DatePeriodPicker
          view="switch"
          defaultMode="month"
          popupPosition="bottom-left"
          value={period}
          setValue={setPeriod}
        />
      </div>
      <div className={s.container}>
        <div
          className={cn(s.chartContainer, passesChart.loading && s.loading, !passesChart.success && s.errorContainer)}
        >
          {passesChart.success ? (
            <>
              <div className={s.chartHeader}>
                <div className={s.chartLabel}>
                  {t('Подключенные')}
                  <br />
                  {t('карты')}
                </div>
                <div className={s.chartValue}>
                  <div className={s.chartValueLabel}>{t('карт за период')}</div>
                  <div className={cn(s.chartNumber, passesChart.diff > 0 ? s.positive : s.negative)}>
                    {t('{{count, number}}', { count: passesChart.diff })}
                  </div>
                </div>
              </div>
              <div className={s.chart}>{!passesChart.loading && <PassesChart {...passesChart} />}</div>
              <div className={s.legend}>
                <div className={cn(s.legendItem, s.blue)}>{t('Выпущено')}</div>
                <div className={cn(s.legendItem, s.purple)}>{t('Активировано')}</div>
                <div className={cn(s.legendItem, s.orange)}>{t('Деактивировано')}</div>
              </div>
            </>
          ) : (
            <div className={s.error}>{t('Ошибка данных')}</div>
          )}
        </div>
        <div
          className={cn(s.chartContainer, ordersChart.loading && s.loading, !ordersChart.success && s.errorContainer)}
        >
          <div className={s.chartHeader}>
            <div className={s.chartLabel}>
              {t('Использование')}
              <br />
              {t('карт')}
            </div>
            <div className={s.chartValue}>
              <div className={s.chartValueLabel}>{t('заказов за период')}</div>
              <div className={cn(s.chartNumber, ordersChart.total > 0 ? s.positive : s.negative)}>
                {t('{{count, number}}', { count: ordersChart.total })}
              </div>
            </div>
          </div>
          <div className={s.chart}>{!ordersChart.loading && <OrdersChart {...ordersChart} />}</div>
          <div className={s.legend}>
            <div className={cn(s.legendItem, s.purple)}>
              {t('1 заказ ({{percent, number}}%)', { percent: ordersChart.once })}
            </div>
            <div className={cn(s.legendItem, s.orange)}>
              {t('2 и более ({{percent, number}}%)', { percent: ordersChart.multi })}
            </div>
          </div>
        </div>
      </div>
    </WithTitle>
  );
};

export default Stats;
